"use client";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Plus } from "lucide-react";
export default function Faq() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const faqs = [{
    question: "What is RevPAR?",
    answer: "RevPAR stands for Revenue Per Available Room, calculated by multiplying a hotel's average daily room rate by its occupancy rate. It is the most critical metric for Indian hoteliers to measure financial performance and room inventory efficiency. Our strategies focus on optimizing this value to ensure your property outperforms the local market."
  }, {
    question: "How to optimize OTA listings?",
    answer: "Optimization involves maintaining high-quality content scores, strategic rate parity, and active review management across platforms like Booking.com and Agoda. We implement dynamic inventory controls and visibility boosters to ensure your property ranks higher in search results. This approach directly leads to increased organic traffic and lower acquisition costs."
  }, {
    question: "Why hire a revenue consultant in India?",
    answer: "The Indian hospitality market is highly fragmented and price-sensitive, requiring localized expertise to navigate seasonal demand shifts. A consultant like Sunil Singh provides data-driven insights that internal teams might miss, such as competitive benchmarking and tech stack integration. We help you bridge the gap between operational costs and maximum yield potential."
  }, {
    question: "How does dynamic pricing work for hotels?",
    answer: "Dynamic pricing uses real-time market data, competitor rates, and historical demand patterns to adjust room prices automatically. By leveraging advanced algorithms, we ensure your rates are always competitive, capturing high-value bookings during peak periods while maintaining occupancy during off-seasons. This proactive strategy eliminates the guesswork from your daily pricing decisions."
  }, {
    question: "What is the impact of RGI on hotel performance?",
    answer: "Revenue Generation Index (RGI) measures your hotel's revenue share compared to its competitive set in the local market. An RGI above 100 indicates you are capturing more than your fair share of revenue, which is our primary goal for every client. We use this metric to fine-tune your distribution strategy and solidify your market leadership."
  }, {
    question: "How to start with HotelRevBaBa?",
    answer: "The process begins with a comprehensive revenue audit to identify leaks in your current distribution and pricing model. Following the audit, we implement a tailored roadmap involving tech stack optimization and hands-on management. Simply book a discovery call to discuss your property's specific challenges and growth goals."
  }];
  return <section data-section-id="6115" id="faqaccordion" className="bg-background text-foreground py-24 md:py-32 px-6 md:px-8 overflow-hidden">
      <div ref={ref} className={`mx-auto max-w-7xl transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="inline-block mb-6">
              <span className="text-[12px] font-mono uppercase tracking-[0.2em] text-secondary font-bold">
                Common Inquiries
              </span>
            </div>
            <h2 className="font-sans font-bold text-4xl md:text-5xl lg:text-6xl leading-[1.1] tracking-tighter mb-8">
              Expert <span className="font-serif italic font-normal text-secondary">insights</span> for hotel owners
            </h2>
            <p className="text-muted-foreground text-lg md:text-xl leading-relaxed max-w-md">
              Strategic answers to the most pressing questions about revenue management, technology integration, and market growth in India.
            </p>
            
            <div className="mt-12 hidden lg:block">
              <div className="p-6 border-l-4 border-secondary bg-secondary/10 rounded-lg">
                <p className="italic font-medium text-foreground/90">
                  "Our goal is to transform your data into a competitive advantage, ensuring every room is priced for maximum yield."
                </p>
                <p className="mt-4 text-sm font-bold uppercase tracking-wider">— Sunil Singh</p>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className="w-full space-y-2">
              {faqs.map((faq, i) => <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-b border-border/60 last:border-b-0">
                  <AccordionTrigger className="py-6 text-left font-sans font-semibold text-lg md:text-xl text-foreground hover:no-underline group transition-colors hover:text-secondary [&>svg]:hidden">
                    <span className="flex-1 pr-8">{faq.question}</span>
                    <div className="relative flex items-center justify-center w-10 h-10 shrink-0 bg-secondary/10 group-data-[state=open]:bg-secondary group-data-[state=open]:text-primary-foreground transition-all duration-300 rounded-lg">
                      <Plus className="w-5 h-5 transition-transform duration-300 group-data-[state=open]:rotate-45" aria-label="Hotel revenue management FAQ icon" />
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-8 pr-12">
                    <div className="text-muted-foreground text-base md:text-lg leading-relaxed max-w-2xl">
                      {faq.answer}
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>

      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer.replace(/<[^>]*>?/gm, '')
          }
        }))
      })
    }} />
    </section>;
}